import i18n from "i18next";
import Fetch from "i18next-fetch-backend";
import { initReactI18next } from "react-i18next";
import { validator } from "safervpn-storybook/src/tools/validator";
import {
  capitalize,
  concatWith,
  env,
  getLanguage,
  I18N_GLOBAL_KEY,
} from "@p81-common/shell-core-js";

// Reader helpers + the GLOBAL_KEY constant moved to shell-core-js — one
// federation singleton, so host + MFEs all read the same window slot. This
// file only owns the host-side initialisation that pulls react-i18next,
// i18next-fetch-backend, and the storybook validator. Re-exported for
// backwards compatibility with existing host imports of `tools/i18n`.
export {
  DEFAULT_LANGUAGE,
  LNG,
  getI18nInstance,
  getLanguage,
  setLanguage,
} from "@p81-common/shell-core-js";

let nameToBrand = {
  sonicwall: "SonicWall",
  perimeter81: "Check Point SASE",
};

let errors = {
  sonicwall: {
    ACCOUNT_EXPIRED:
      "Your SonicWall Cloud Edge is expired ... <a href={{supportEmail, link}} target='_blank'>contact support</a>",
  },
  perimeter81: {
    ACCOUNT_EXPIRED:
      "Something went wrong with your Check Point SASE workspace. Please contact your account admin.",
  },
};

const formatters = {
  lowercase(value) {
    return value.toLowerCase();
  },
  uppercase(value) {
    return value.toUpperCase();
  },
  capitalize(value) {
    return capitalize(value);
  },
  capitalizeAll(value) {
    return value.replaceAll(/(\w+)/g, capitalize);
  },
  link(value) {
    if (validator.isEmail(value)) {
      return `mailto:${value}`;
    }
    if (validator.isURL(value)) {
      return value;
    }
    return "";
  },
  withSpace(value) {
    return value ? `${value} ` : value;
  },
  removeUnderscore(value) {
    return value.replace("_", " ");
  },
  with(value, lng, options) {
    const { prefix, postfix } = options;
    return value ? concatWith("", prefix, value, postfix) : "";
  },
  array(value, lng, options) {
    const { nameKey = "name", valueKey = "value" } = options;
    return value
      ? value.map((item) => `${item[nameKey]} = ${item[valueKey]}`)
      : "";
  },
  join(value) {
    return Array.isArray(value) ? value.join(", ") : value ?? "";
  },
};

// Namespaces fetched during i18n init, before any MFE mounts. A lazy namespace's
// load does not always survive the module-federation boundary, so anything needed
// on first paint belongs here.
export const EAGER_NAMESPACES = [
  "activity",
  "breadcrumbs",
  "common",
  "errors",
  "navigation",
  "notifications",
  // Eager-load `welcome` (the whole Get Started v2 wizard reads it). It used to be lazy-loaded
  // only when an early welcome/intro popup rendered; booting straight into an MFE (e.g. hard
  // refresh on the IA Access Policy page) skips those, so navigating to Get Started hit an
  // unloaded namespace whose lazy load + re-render doesn't survive the module-federation
  // boundary — labels rendered as raw `welcome:*` keys (P81-132635).
  "welcome",
];

function i18nSetup(config) {
  let { whitelabel, supportEmail } = config;
  let brand = nameToBrand[whitelabel];
  let translationsVersion = env.REACT_APP_GIT_TAG || "";
  let options = {
    backend: {
      loadPath: `${
        process.env.REACT_APP_ENVS_URL || ""
      }/translations/{{lng}}/{{ns}}.json?v=${translationsVersion}`,
      requestOptions: {
        // i18next retries a failed load on its own, but `force-cache` served
        // every retry the same cached error instantly, so the namespace stayed
        // broken until the cache was cleared. `no-cache` revalidates each time,
        // so the retries reach the origin. `default` is not enough: it reuses a
        // still-fresh entry, and an error with a max-age is fresh.
        cache: "no-cache",
      },
    },

    lng: getLanguage(),

    fallbackLng: "en",
    load: "currentOnly",
    // `defaultNS: "common"` makes unprefixed `t()` calls fallback to `common`.
    // The previous `defaultNS: []` worked when `useTranslation("common")` +
    // `t("some.key")` relied on the hook to bind the namespace, but that
    // binding doesn't survive the MF federation boundary for host
    // components consumed inside the MFE — `t("attentionBox...")` rendered
    // as the raw key. Explicitly defaulting to `common` fixes those; calls
    // that use prefixed keys (`t("network:FOO")`) still override correctly.
    defaultNS: "common",
    ns: EAGER_NAMESPACES,

    react: {
      // useSuspense: false,
      wait: true, // globally set to wait for loaded translations in translate hoc
      // exposeNamespace: true // exposes namespace on data-i18next-options to be used in eg. locize-editor
    },

    // debug:
    //   ['staging', 'development'].indexOf(process.env.REACT_APP_ENVIRONMENT) >
    //   -1,

    // cache: {
    //   enabled: true
    // },

    interpolation: {
      escapeValue: false, // not needed for react!!
      formatSeparator: ",",
      defaultVariables: {
        brand,
        connectorName: brand === "SonicWall" ? "WireGuard" : brand,
        supportEmail,
        linkToSignIn:
          brand === "SonicWall"
            ? "//app.sonicwalledge.com/find-workspace"
            : `//app.${env.REACT_APP_P81_DOMAIN}/sign-in`,
        ...errors[whitelabel],
      },
    },
  };
  if (typeof window !== "undefined") {
    window[I18N_GLOBAL_KEY] = i18n;
  }
  const result = i18n.use(Fetch).use(initReactI18next).init(options);
  Object.entries(formatters).forEach(([name, formatter]) => {
    i18n.services.formatter.add(name, formatter);
  });

  return result;
}

export default i18nSetup;
